import React, { Component } from "react";
import clsx from "clsx";
import { TextField, Chip, CircularProgress } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Button, Tooltip } from "@material-ui/core";
import { FaUndo } from "react-icons/fa";
import { connect } from "react-redux";
import axios from "axios";

class BulkEditTextInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialErrors: props.errors,
      updatedError: "",
      keywords: [],            // all available keywords from backend
      selectedKeywords: [],    // currently selected keywords in the UI
      keywordsLoading: false,  // loading state for keywords
    };
    this.keywordSearchDebounce = null;
    // If editing is already present at mount time, prime selectedKeywords
    if (props.editing && props.editing.keywords) {
      this.state.selectedKeywords = this.prepareTagsForDisplay(
        props.editing.id,
        props.editing.keywords
      );
    }

  }

  async componentDidMount() {
    this.fetchKeywords("", 20);
    // If editing was already available use it (constructor handled that).
    // Optional: re-run for consistency.
    const { editing } = this.props;
    if (editing && editing.keywords) {
      const selectedKeywords = this.prepareTagsForDisplay(
        editing.id,
        editing.keywords
      );
      this.setState({ selectedKeywords });
    }
  }

  componentDidUpdate(prevProps) {
    // Editing became available or changed
    if (this.props.editing !== prevProps.editing && this.props.editing) {
      const { editing } = this.props;
      const selectedKeywords = this.prepareTagsForDisplay(
        editing.id,
        editing.keywords || []
      );
      this.setState({ selectedKeywords });
    }
  }

  handleRefresh = () => {
    const { refreshOnClick } = this.props;
    refreshOnClick();
  };

  fetchWithCancel = async (fields, query, size = 10) => {
    const options = {
      headers: {
        Authorization: `${localStorage.auth0IdToken}`
      },
    };

    try {
      var res = await axios.post("/tracksuggestions", { fields, query, size }, options);
      return res.data.payload;
    }
    catch (err) {
      console.error(fields, err);
      return [];
    }
  };

  fetchKeywords = async (query = '', size = 10) => {
    this.setState({ keywordsLoading: true });
    var mapped = [];
    try {
      const data = await this.fetchWithCancel(
        ['keywords'], query, size
      );
      if (query.trim() == '' || data?.["keywords"]?.some(k => k.label?.trim()?.toLowerCase() == query?.trim()?.toLowerCase())) {
        mapped = (data?.["keywords"] || []).map(k => ({ type: 'keyword', label: k.label }));
      }
      else {
        mapped = [{ type: 'keyword', label: query, isNew: true }, ...((data?.["keywords"] || []).map(k => ({ type: 'keyword', label: k.label })))];
      }
    } finally {
      this.setState({ keywords: mapped, keywordsLoading: false });
    }
  };

  prepareTagsForDisplay = (trackId, keywords) => {
    const {
      data: { job },
    } = this.props;

    if (!trackId) return [];
    const specificUpdates =
      (job?.track_specific_updates || {})[trackId] || {};

    const taggingUpdates = specificUpdates.tagging_updates || {};

    let newKeywords = (keywords || []).map(k => ({
      type: 'keyword',
      label: k
    }));

    if (Array.isArray(taggingUpdates.keywords_remove) && taggingUpdates.keywords_remove.length) {
      const removeSet = new Set(
        taggingUpdates.keywords_remove.map(
          kr => kr.label?.trim()?.toLowerCase()
        )
      );
      newKeywords = newKeywords.filter(
        k => !removeSet.has(k.label?.trim()?.toLowerCase())
      );
    }

    if (Array.isArray(taggingUpdates.keywords_add) && taggingUpdates.keywords_add.length) {
      taggingUpdates.keywords_add.forEach(k => {
        const lbl = k.label?.trim();
        if (
          lbl &&
          !newKeywords.some(
            existing => existing.label?.trim()?.toLowerCase() ===
              lbl.toLowerCase()
          )
        ) {
          newKeywords.push({ type: 'keyword', label: lbl });
        }
      });
    }
    return newKeywords;
  };

  // ---------- Debounced input handlers ----------
  handleKeywordInputChange = (event, value, reason) => {
    const {
      handleKeywordChange
    } = this.props;

    var lowerValue = value?.trim().toLowerCase() || '';
    if (value && value.includes(',')) {
      const parts = value.split(',').map(s => s.trim()).filter(Boolean);
      const deduped = [...this.state.selectedKeywords];
      if (parts.length > 0) {
        const seen = new Set(deduped.map(k => k.label?.toLowerCase()));
        parts.forEach(part => {
          const key = part.toLowerCase();
          if (!seen.has(key)) {
            deduped.push({ type: 'keyword', label: part });
            seen.add(key);
          }
        });
        handleKeywordChange(
          this.props.editing.id,
          deduped
        );
      }
      this.setState({ selectedKeywords: deduped, keywordSearch: '' });
      lowerValue = '';
    }

    if (lowerValue !== this.state.keywordSearch && reason === 'input') {
      clearTimeout(this.keywordSearchDebounce);
      this.setState({ keywordSearch: lowerValue }, () =>
        this.keywordSearchDebounce = setTimeout(() => this.fetchKeywords(lowerValue), 350)
      );
    }
  };

  onKeywordChange = (event, value) => {

    const {
      handleKeywordChange
    } = this.props;
    const deduped = [];
    const seen = new Set();

    (value || []).forEach(item => {
      if (item == null || !item.label) return;

      const rawLabel = item.label;
      const clean = (rawLabel || '').trim();
      if (!clean) return;

      const key = clean.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);

      // Preserve other props (e.g. isNew) from first instance
      deduped.push(
        typeof item === 'string'
          ? { type: 'keyword', label: clean }
          : { ...item, label: clean }
      );
    });

    this.setState({ selectedKeywords: deduped });

    handleKeywordChange(
      this.props.editing.id,
      deduped
    );

    // Optionally refresh suggestions after selection change
    this.fetchKeywords("", 20);
  };

  render() {
    const {
      classes,
      title,
      name,
      refreshDisabled,
      isRequired,
      isAppend,
    } = this.props;
    const {
      keywords,
      selectedKeywords,
      keywordsLoading } = this.state;

    return (
      <>
        <label className={classes.globalCreateLabel} htmlFor={title}>
          {title}
          {isRequired && (
            <span aria-label="Name required" className={classes.reqSymbol}>
              {" "}
              *{" "}
            </span>
          )}
          {isAppend && (
            <span className={clsx(classes.append_text_color)}>
              <b> +</b>{" "}
            </span>
          )}
        </label>
        <span>:</span>
        <div className="flexItem">
          <Autocomplete
            multiple
            options={keywords}
            value={selectedKeywords}
            loading={keywordsLoading}
            onInputChange={this.handleKeywordInputChange}
            onChange={this.onKeywordChange}
            getOptionLabel={(o) => o.label || ''}
            renderOption={(option) => (
              <span>{option.label}
                <span style={{ color: 'yellow' }}>{option.isNew && ' (new)'}</span>
              </span>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip
                    {...tagProps}
                    label={option.label}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"

                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {keywordsLoading ? <CircularProgress color="inherit" size={8} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {!refreshDisabled && (
            <Tooltip title={"Reset to default value"}>
              <Button
                aria-label={name}
                id={name}
                name={name}
                //disabled={refreshDisabled}
                onClick={this.handleRefresh}
                className={classes.resetButton}>
                <FaUndo style={{ fontSize: "10px" }} />
              </Button>
            </Tooltip>
          )}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  data: state.bulkEdit,
});

export default connect(mapStateToProps)(BulkEditTextInput);
